/**
 * Nested AND/OR filter builder (FS-029).
 *
 * Notion's shape: a group is "match all" or "match any", a row is field /
 * operator / value, and a group can hold another group up to three deep. The
 * depth cap is the server's, read from the filter reference rather than
 * repeated here — and the "add group" button disappears at the limit instead
 * of letting someone build a filter the server will refuse.
 */

import { Plus, Trash2 } from "lucide-react";
import {
  MAX_DEPTH,
  VALUELESS_OPS,
  type FilterNode,
  type FilterOp,
} from "@/features/inventory/filterTree";
import type { FilterReference } from "@/features/inventory/api";

interface Props {
  node: FilterNode;
  onChange: (node: FilterNode) => void;
  onRemove?: () => void;
  reference?: FilterReference;
  depth?: number;
}

const OP_LABELS: Record<string, string> = {
  eq: "is",
  ne: "is not",
  gt: "is over",
  gte: "is at least",
  lt: "is under",
  lte: "is at most",
  in: "is one of",
  not_in: "is not one of",
  contains: "contains",
  starts_with: "starts with",
  ends_with: "ends with",
  between: "is between",
  is_null: "is empty",
  is_not_null: "is set",
  similar_to: "sounds like",
};

function isGroup(
  node: FilterNode,
): node is { all: FilterNode[] } | { any: FilterNode[] } {
  return "all" in node || "any" in node;
}

function groupKey(
  node: { all: FilterNode[] } | { any: FilterNode[] },
): "all" | "any" {
  return "all" in node ? "all" : "any";
}

function children(
  node: { all: FilterNode[] } | { any: FilterNode[] },
): FilterNode[] {
  return "all" in node ? node.all : node.any;
}

/** A blank row, with a field the server definitely accepts. */
function newLeaf(reference?: FilterReference): FilterNode {
  return { field: reference?.fields[0] ?? "status", op: "eq", value: "" };
}

function FilterGroupBuilder({
  node,
  onChange,
  onRemove,
  reference,
  depth = 1,
}: Props) {
  if (!isGroup(node)) {
    return (
      <LeafRow
        leaf={node as { field: string; op: FilterOp; value?: unknown }}
        onChange={onChange}
        onRemove={onRemove}
        reference={reference}
      />
    );
  }

  const key = groupKey(node);
  const parts = children(node);
  const canNest = depth < MAX_DEPTH;

  const replace = (index: number, next: FilterNode) => {
    const copy = [...parts];
    copy[index] = next;
    onChange({ [key]: copy } as FilterNode);
  };

  const removeAt = (index: number) => {
    onChange({ [key]: parts.filter((_, i) => i !== index) } as FilterNode);
  };

  return (
    <div className="rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-sunken)] p-2.5">
      <div className="mb-2 flex items-center gap-2">
        <select
          value={key}
          onChange={(event) =>
            onChange({ [event.target.value]: parts } as FilterNode)
          }
          aria-label="Match all or any"
          className="h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-1.5 text-xs font-medium text-[var(--text-primary)]"
        >
          <option value="all">Match all</option>
          <option value="any">Match any</option>
        </select>
        <span className="text-xs text-[var(--text-tertiary)]">
          of the following
        </span>
        <div className="ml-auto flex items-center gap-1.5">
          <button
            type="button"
            onClick={() =>
              onChange({ [key]: [...parts, newLeaf(reference)] } as FilterNode)
            }
            className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-1 text-xs text-brand-600 hover:bg-[var(--color-surface-hover)]"
          >
            <Plus className="h-3 w-3" /> Condition
          </button>
          {canNest && (
            <button
              type="button"
              onClick={() =>
                onChange({
                  [key]: [...parts, { all: [newLeaf(reference)] }],
                } as FilterNode)
              }
              className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] px-1.5 py-1 text-xs text-brand-600 hover:bg-[var(--color-surface-hover)]"
            >
              <Plus className="h-3 w-3" /> Group
            </button>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={onRemove}
              aria-label="Remove group"
              className="rounded-[var(--radius-sm)] p-1 text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
            >
              <Trash2 className="h-3 w-3" />
            </button>
          )}
        </div>
      </div>

      {parts.length === 0 ? (
        <p className="px-1 py-2 text-xs text-[var(--text-tertiary)]">
          Nothing in this group yet.
        </p>
      ) : (
        <div className="space-y-1.5">
          {parts.map((part, index) => (
            <FilterGroupBuilder
              key={index}
              node={part}
              depth={depth + 1}
              reference={reference}
              onChange={(next) => replace(index, next)}
              onRemove={() => removeAt(index)}
            />
          ))}
        </div>
      )}

      {!canNest && (
        <p className="mt-2 px-1 text-[11px] text-[var(--text-tertiary)]">
          Groups stop at {MAX_DEPTH} deep — past that a filter is hard to read
          back, and two saved views are clearer than one nobody understands.
        </p>
      )}
    </div>
  );
}

function LeafRow({
  leaf,
  onChange,
  onRemove,
  reference,
}: {
  leaf: { field: string; op: FilterOp; value?: unknown };
  onChange: (node: FilterNode) => void;
  onRemove?: () => void;
  reference?: FilterReference;
}) {
  const computed = reference?.computed_fields.find(
    (field) => field.key === leaf.field,
  );
  const allowedOps = computed
    ? (reference?.operators_by_kind[computed.kind] ??
      reference?.operators ??
      [])
    : (reference?.operators ?? []);
  const needsValue = !VALUELESS_OPS.includes(leaf.op);

  return (
    <div className="flex flex-wrap items-center gap-1.5 rounded-[var(--radius-sm)] bg-[var(--surface-base)] px-2 py-1.5">
      <select
        value={leaf.field}
        onChange={(event) => onChange({ ...leaf, field: event.target.value })}
        aria-label="Field"
        className="h-7 max-w-[11rem] rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-1.5 text-xs text-[var(--text-primary)]"
      >
        {reference?.computed_fields.length ? (
          <optgroup label="Calculated">
            {reference.computed_fields.map((field) => (
              <option
                key={field.key}
                value={field.key}
                title={field.description}
              >
                {field.label}
              </option>
            ))}
          </optgroup>
        ) : null}
        <optgroup label="Fields">
          {(reference?.fields ?? []).map((field) => (
            <option key={field} value={field}>
              {field.replace(/_/g, " ")}
            </option>
          ))}
        </optgroup>
      </select>

      <select
        value={leaf.op}
        onChange={(event) =>
          onChange({ ...leaf, op: event.target.value as FilterOp })
        }
        aria-label="Operator"
        className="h-7 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-1.5 text-xs text-[var(--text-primary)]"
      >
        {allowedOps.map((op) => (
          <option key={op} value={op}>
            {OP_LABELS[op] ?? op}
          </option>
        ))}
      </select>

      {needsValue && (
        <input
          value={String(leaf.value ?? "")}
          onChange={(event) => onChange({ ...leaf, value: event.target.value })}
          placeholder={
            leaf.op === "in" || leaf.op === "not_in" ? "a, b, c" : "value"
          }
          aria-label="Value"
          className="h-7 w-32 rounded-[var(--radius-sm)] border border-[var(--border-default)] bg-[var(--surface-base)] px-1.5 text-xs text-[var(--text-primary)]"
        />
      )}

      {computed && (
        <span
          className="text-[11px] text-[var(--text-tertiary)]"
          title={computed.description}
        >
          {computed.kind}
        </span>
      )}

      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove condition"
          className="ml-auto rounded-[var(--radius-sm)] p-1 text-[var(--text-tertiary)] hover:text-[var(--text-danger)]"
        >
          <Trash2 className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export default FilterGroupBuilder;
